import React, {
    useEffect,
    useState,
    useRef,
    useCallback,
    useImperativeHandle,
    forwardRef,
} from 'react';
import { Terminal, TerminalHandle, TerminalProps } from './Terminal';
import "./TerminalWorkspace.scss";
import { apiGet } from "./api";
import moment from "moment/moment";
import { saveAs } from "file-saver";

/** ---------- Типы контекста и вкладок ---------- */
export type OpenContext = {
    serverId?: string;
    peerId?: string;
    uuid?: string;
    apiGetChunkUrl?: string;
    params?: any;
    title?: string;
    terminalInstance?: string;
};

export type TabMeta = {
    id: string;
    title: string;
    context: OpenContext;
};

type MenuItem = {
    peer_id: string;
    server_id: string;
    title: string;
    uuid: string;
    id: string;
    category: string;
    job_url: string;
};


type ActiveJobMetadata = {
    obj_id: string;
};

/** ---------- Пропсы компонента ---------- */
export type TerminalWorkspaceProps = {
    id: string;

    /** DATA / API */
    apiExecuteUrl?: string;
    apiLogsUrl?: string;
    apiActiveJobsUrl?: string;
    fetchActiveJobs?: (params?: any) => Promise<any[]>;
    getJobsPollingIntervalMs?: number;
    terminalInstance?: string;

    /** TABS */
    allowTabs?: boolean;
    initialTabs?: (OpenContext & { title: string })[];
    tabTitleResolver?: (ctx: OpenContext) => string;
    persistStateKey?: string;

    /** TERMINAL */
    terminalProps?: Omit<TerminalProps, 'params' | 'apiGetChunkUrl'>;
    executeParams?: any;
    terminalHeight?: string;
    transformLink?: (text: string) => string;

    /** JOBS MENU */
    showJobsMenu?: boolean;
    onOpenJobAsTab?: (job: any) => OpenContext;
    activeJobData?: ActiveJobMetadata;

    /** UI / I18N */
    i18n?: Partial<{
        executionTabTitle: string;
        downloadTooltip: string;
        jobsMenuTitle: string;
        openTabLabel: string;
        hideJobsTooltip: string;
        showJobsTooltip: string;
    }>;
    icons?: Partial<{ download: string; jobs: string; close: string; external: string }>;

    /** EXPORT */
    enableDownload?: boolean;
    filenameBuilder?: (date: Date, tab: TabMeta, text: string) => string;
    onLogsDownloaded?: (info: { tabId: string; bytes: number }) => void;

    /** CALLBACKS */
    onTabOpen?: (tab: TabMeta) => void;
    onTabClose?: (tab: TabMeta) => void;
    onTabRename?: (tab: TabMeta, newTitle: string) => void;
    onActiveTabChange?: (tab: TabMeta) => void;
};

/** ---------- Императивный API ---------- */
export type TerminalWorkspaceHandle = {
    getExecuteTab: () => TerminalHandle | null;
    getActiveTab: () => TabMeta | null;
    getTerminalHandle: (tabId: string) => TerminalHandle | null;
};

export const TerminalWorkspace = forwardRef<TerminalWorkspaceHandle, TerminalWorkspaceProps>(
    (props, ref) => {
        const {
            allowTabs = true,
            showJobsMenu = true,
            apiExecuteUrl = '/automation/api/executor/',
            apiLogsUrl = '/automation/api/stream_logs/',
            apiActiveJobsUrl = '/automation/api/active_jobs/',
            getJobsPollingIntervalMs = 1500,
            terminalProps,
            executeParams,
            terminalHeight = '60vh',
            enableDownload = true,
            i18n = {
                executionTabTitle: 'Выполнение',
                downloadTooltip: 'Скачать логи',
                jobsMenuTitle: 'Задачи в процессе',
                openTabLabel: 'Открыть вкладку',
                hideJobsTooltip: 'Скрыть',
                showJobsTooltip: 'Показать',
            },
            terminalInstance = "PolicyExecutor",
        } = props;

        const EXECUTE_TAB_ID = 'execute';

        const initTabsOnce = useRef<boolean>(false);

        const buildInitialTabs = (): TabMeta[] => {
            if (props.persistStateKey) {
                try {
                    const raw = localStorage.getItem(`tw:${props.persistStateKey}`);
                    if (raw) {
                        const { tabs: savedTabs } = JSON.parse(raw) as {
                            tabs: TabMeta[];
                            activeTabId: string;
                        };
                        if (Array.isArray(savedTabs) && savedTabs.length) return savedTabs;
                    }
                } catch { }
            }

            return [
                {
                    id: EXECUTE_TAB_ID,
                    title: i18n.executionTabTitle ?? 'Выполнение',
                    context: { params: executeParams, apiGetChunkUrl: apiExecuteUrl, terminalInstance: terminalInstance },
                },
                ...(props.initialTabs?.map((t, i) => ({
                    id: `init_${i}`,
                    title: t.title,
                    context: t,
                })) ?? []),
            ];
        };

        const [tabs, setTabs] = useState<TabMeta[]>(() => buildInitialTabs());
        const [activeTabId, setActiveTabId] = useState<string>(() => {
            if (props.persistStateKey) {
                try {
                    const raw = localStorage.getItem(`tw:${props.persistStateKey}`);
                    if (raw) {
                        const { activeTabId } = JSON.parse(raw) as {
                            tabs: TabMeta[];
                            activeTabId: string;
                        };
                        if (activeTabId) return activeTabId;
                    }
                } catch { }
            }
            return buildInitialTabs()[0]?.id ?? EXECUTE_TAB_ID;
        });

        useEffect(() => {
            if (!initTabsOnce.current) initTabsOnce.current = true;
        }, []);

        const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];

        useEffect(() => {
            if (!props.persistStateKey) return;
            try {
                const payload = JSON.stringify({ tabs, activeTabId });
                localStorage.setItem(`tw:${props.persistStateKey}`, payload);
            } catch { }
        }, [tabs, activeTabId, props.persistStateKey]);

        const terminalRefs = useRef<Record<string, TerminalHandle | null>>({});
        const setTerminalRef = useCallback(
            (tabId: string) =>
                (inst: TerminalHandle | null): void => {
                    terminalRefs.current[tabId] = inst;
                },
            [],
        );

        const [runJobs, setRunJobs] = useState<MenuItem[]>([]);
        const [countJobs, setCountJobs] = useState<number>(0);

        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const menuRef = useRef<HTMLDivElement | null>(null);
        const menuBtnRef = useRef<HTMLDivElement | null>(null);

        const fetchJobs = useCallback(() => {
            return apiGet(apiActiveJobsUrl, { ...(props.activeJobData) }).then((resp: any) => {
                setRunJobs(resp.items);
                setCountJobs(resp.count);
            });
        }, [apiActiveJobsUrl, props.activeJobData]);

        useEffect(() => {
            if (!showJobsMenu || !isMenuOpen) return;
            fetchJobs();
            const timer = window.setInterval(fetchJobs, getJobsPollingIntervalMs);
            return () => window.clearInterval(timer);
        }, [isMenuOpen, showJobsMenu, fetchJobs, getJobsPollingIntervalMs]);

        useEffect(() => {
            const onDocClick = (e: MouseEvent) => {
                if (!isMenuOpen) return;
                const target = e.target as Node;
                if (menuRef.current?.contains(target)) return;
                if (menuBtnRef.current?.contains(target)) return;
                setIsMenuOpen(false);
            };
            document.addEventListener('mousedown', onDocClick);
            return () => document.removeEventListener('mousedown', onDocClick);
        }, [isMenuOpen]);

        const handleCloseTab = useCallback(
            (tabId: string) => {
                setTabs((prev) => {
                    const idx = prev.findIndex((t) => t.id === tabId);
                    if (idx === -1) return prev;
                    const next = prev.filter((t) => t.id !== tabId);

                    if (activeTabId === tabId) {
                        const fallback = next[Math.max(0, idx - 1)] ?? next[0];
                        if (fallback) setActiveTabId(fallback.id);
                    }

                    delete terminalRefs.current[tabId];
                    const closed = prev[idx];
                    if (closed) props.onTabClose?.(closed);

                    return next;
                });
            },
            [activeTabId, props],
        );

        const handleOpenLinkFromTerminal = useCallback(
            (link: string) => {
                const last = link.split('/').pop() ?? '';
                const [server_id, uuid] = last.split('_');
                const id = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
                const title = server_id || 'HOST_LOG';

                const newTab: TabMeta = {
                    id,
                    title,
                    context: {
                        params: {
                            ...(activeTab?.context?.params ?? {}),
                            ...(executeParams ?? {}),
                            uuid,
                            "server_id": server_id,
                            "category": "HOST_LOGS"
                        },
                        apiGetChunkUrl: apiLogsUrl,
                    },
                };
                setTabs((prev) => [...prev, newTab]);
                setActiveTabId(id);
                props.onTabOpen?.(newTab);
            },
            [activeTab?.context?.params, executeParams, apiLogsUrl, props],
        );

        const openItemAsTab = useCallback(
            (job: MenuItem) => {
                const id = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
                const ctx: OpenContext = props.onOpenJobAsTab
                    ? props.onOpenJobAsTab(job)
                    : {
                        title: job.server_id,
                        apiGetChunkUrl: apiLogsUrl,
                        params: {
                            ...(executeParams ?? {}),
                            server_id: job.server_id,
                            uuid: job.uuid,
                            category: job.category,
                        },
                    };

                const title = ctx.title ?? job.server_id ?? 'JOB';
                const newTab: TabMeta = { id, title, context: ctx };
                setTabs((prev) => [...prev, newTab]);
                setActiveTabId(id);
                setIsMenuOpen(false);
                props.onTabOpen?.(newTab);
            },
            [props, apiLogsUrl, executeParams],
        );

        const downloadLog = useCallback(() => {
            if (!enableDownload) return;
            const handle = terminalRefs.current[activeTabId];
            const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
            const content = new Blob([handle?.getTextContent() || ""], { type: "text/plain;charset=utf-8" });
            const dateTime = moment(new Date()).format("YYYY-MM-DDTHH-mm-ss");
            const fileName = `${activeTab.title} ${dateTime}.log`;
            saveAs(content, fileName);

        }, [activeTabId, enableDownload, tabs]);

        /** ---------- Императивный API ---------- */
        useImperativeHandle(ref, () => ({
            getExecuteTab: () => terminalRefs.current[EXECUTE_TAB_ID] ?? null,
            getActiveTab: () => activeTab ?? null,
            getTerminalHandle: (tabId) => terminalRefs.current[tabId] ?? null,
        }));

        useEffect(() => {
            if (activeTab) props.onActiveTabChange?.(activeTab);
        }, [activeTabId, activeTab, props]);

        /** ---------- Рендер ---------- */
        return (
            <div className="terminal-workspace">
                <div className="tw-windows__content">
                    <div className="tw-wrapper">
                        {allowTabs && (
                            <div className="tw-tabs">
                                <div className="tw-tabs__list">
                                    {tabs.map((t) => (
                                        <div
                                            key={t.id}
                                            className={`tw-tab ${t.id === activeTabId ? 'is-active' : ''}`}
                                            onClick={() => setActiveTabId(t.id)}
                                        >
                                            <span className="tw-tab__title">{t.title}</span>
                                            {tabs.length > 1 && t.id !== EXECUTE_TAB_ID && (
                                                <button
                                                    className="tw-tab__close"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCloseTab(t.id);
                                                    }}
                                                    aria-label="Close tab"
                                                    type="button"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="tw-tabs__actions">
                                    <div
                                        style={{ marginRight: "12px" }}
                                        className="tw-tabs__icon"
                                        title={i18n.downloadTooltip}
                                        onClick={downloadLog}
                                        role="button"
                                        aria-label={i18n.downloadTooltip}
                                    >
                                        <i className="fas fa-arrow-alt-to-bottom" />
                                    </div>

                                    {showJobsMenu && (
                                        <div
                                            ref={menuBtnRef}
                                            className={`tw-tabs__icon tw-tabs__icon--menu ${isMenuOpen ? 'is-active' : ''
                                                }`}
                                            title={
                                                isMenuOpen
                                                    ? i18n.hideJobsTooltip ?? 'Скрыть'
                                                    : i18n.showJobsTooltip ?? 'Показать'
                                            }
                                            onClick={() => setIsMenuOpen((v) => !v)}
                                            aria-haspopup="true"
                                            aria-expanded={isMenuOpen}
                                            role="button"
                                        >
                                            <i className="fas fa-list" />
                                        </div>
                                    )}

                                    {showJobsMenu && isMenuOpen && (
                                        <div ref={menuRef} className="tw-menu tw-menu--right">
                                            <div className="tw-menu__header d-flex justify-content-between">
                                                <span>
                                                    {i18n.jobsMenuTitle ?? 'Задачи в процессе выполнения'}
                                                </span>
                                                <span>{countJobs ? countJobs : ""}</span>

                                            </div>
                                            <div className="tw-menu__list">
                                                {runJobs.map((job) => (
                                                    <div
                                                        key={job.id}
                                                        className="tw-menu__item d-flex justify-content-between align-items-center"
                                                        title={job.title}
                                                        onClick={() => openItemAsTab(job)}
                                                    >
                                                        <div>
                                                            <span className="tw-menu__item-title">{job.server_id}</span>
                                                            <div className="tw-menu__links">
                                                                <span
                                                                    className="tw-menu__link tw-menu__link--terminal"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openItemAsTab(job);
                                                                    }}
                                                                >
                                                                    {i18n.openTabLabel ?? 'открыть вкладку'}
                                                                </span>
                                                                <i
                                                                    className="fal fa-external-link tw-menu__icon-link"
                                                                    title="Открыть задачу"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        window.open(
                                                                            job.job_url,
                                                                            '_blank',
                                                                            'noopener,noreferrer',
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="tw-menu__spinner">
                                                            <div
                                                                className="spinner-border spinner-border-sm text-success"
                                                                role="status"
                                                            >
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {tabs.length > 0 && (
                            <div className="tw-terminal-stack">
                                {tabs.map((t) => (
                                    <div
                                        key={t.id}
                                        className={`tw-terminal ${t.id === activeTabId ? 'is-active' : ''}`}
                                        style={{ display: t.id === activeTabId ? 'block' : 'none' }}
                                    >
                                        <Terminal
                                            instance={t.context.terminalInstance || "terminal"}
                                            ref={setTerminalRef(t.id)}
                                            params={t.context.params}
                                            apiGetChunkUrl={t.context.apiGetChunkUrl ?? apiLogsUrl}
                                            height={terminalHeight}
                                            {...terminalProps}
                                            onClickLinkCallback={handleOpenLinkFromTerminal}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);

TerminalWorkspace.displayName = 'TerminalWorkspace';
export default TerminalWorkspace;
